// hooks/ useSessionManager.js
import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { performLogout } from '../../utils/logoutHandler';

const API_URL = process.env.REACT_APP_API_URL;
const IDLE_TIMEOUT = 30 * 60 * 1000; // 30 min
const MODAL_TIMEOUT = 15 * 60 * 1000; // 10 min
// const IDLE_TIMEOUT = 5 * 60 * 1000;  // 1 minutes
// const MODAL_TIMEOUT = 15 * 1000;     // 15 seconds


const loginRoutes = ['/login', '/superadmin_login', '/internal_staff_login', '/tprm_login', '/tprm_user_login', '/vrm_login', '/offsec_staff_login', '/audit_login', '/set_password', '/cara_login', '/reset_password', '/expired-link'];
const roleRedirectMap = {
  TPRM: '/tprm_login',
  TPRMUSER: '/tprm_user_login',
  VRM: '/vrm_login',
  OFFSEC_STAFF: '/offsec_staff_login',
  AUDIT: '/audit_login',
  CARA: '/cara_login',
  DEFAULT: '/login',
  SUPERADMIN: '/superadmin_login',
    INTERNALSTAFF: '/internal_staff_login'
};

const useSessionManager = () => {
  const [isSessionValid, setIsSessionValid] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const navigate = useNavigate();
  const idleTimer = useRef(null);
  const modalTimer = useRef(null);
  const lastResetTime = useRef(0);
  const location = useLocation();
  const broadcastChannel = useRef(null);
  const showModalRef = useRef(false);

  // Sync ref with state
  useEffect(() => {
    showModalRef.current = showModal;
  }, [showModal]);

  const redirectToLogin = useCallback(() => {
    const role = localStorage.getItem('role');
    const target = roleRedirectMap[role] || roleRedirectMap.DEFAULT;
    if (location.pathname !== target) {
      navigate(target);
    }
  }, [navigate, location.pathname]);

  const handleLogout = useCallback(async () => {
    if (loggingOut) return;
    setLoggingOut(true);

    const now = new Date().toLocaleTimeString();
    console.log(`🚪 [${now}] handleLogout called - Starting logout process...`);

    try {
      setIsSessionValid(false);

      // Get user info from localStorage before clearing
      const role = localStorage.getItem('role');
      const userType = localStorage.getItem('user_type');

      // Use centralized logout handler for immediate cross-tab synchronization
      await performLogout(userType, role);

      console.log(`🚪 [${now}] performLogout completed successfully`);

    } catch (err) {
      console.error(`🚪 [${now}] Logout failed:`, err);
      // Force redirect even on error
      redirectToLogin();
    } finally {
      setLoggingOut(false);
      console.log(`🚪 [${now}] Logout process finished`);
    }
  }, [loggingOut, redirectToLogin]);

  const handleInvalidSession = useCallback(() => {
    setIsSessionValid(false);
    handleLogout();
  }, [handleLogout]);

  const checkSessionValidity = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}api/check-session/`, {
        withCredentials: true,
      });

      if (response.data.status === 'active') {
        setIsSessionValid(true);
        // AuthGuard handles redirects, so we don't need to redirect here
      } else if (response.data.status === 'pending_2fa') {
        // Session requires 2FA verification
        setIsSessionValid(false); // Don't set as valid until OTP is verified
        // AuthGuard handles OTP redirects
      } else {
        handleInvalidSession();
      }
    } catch (error) {
      console.error('Error checking session:', error);
      handleInvalidSession();
    }
  }, [handleInvalidSession]);

  // Enhanced cross-tab synchronization
  const resetIdleTimer = useCallback(() => {
    const nowTime = Date.now();
    const now = new Date().toLocaleTimeString();

    //  CRITICAL: Don't reset timer if modal is showing
    // This prevents the modal from disappearing when user moves mouse to click "Continue"
    if (showModalRef.current) {
      console.log(` [${now}] Modal is showing - ignoring timer reset`);
      return;
    }

    // Throttle: Only reset if at least 1 second has passed since last reset
    if (nowTime - lastResetTime.current < 1000) {
      console.log(`🕒 [${now}] Activity detected but throttled (${nowTime - lastResetTime.current}ms since last reset)`);
      return;
    }

    lastResetTime.current = nowTime;
    console.log(`🕒 [${now}] Activity detected! Resetting idle timer...`);
    console.log(`🕒 [${now}] Current timers - idleTimer: ${idleTimer.current}, modalTimer: ${modalTimer.current}`);

    clearTimeout(idleTimer.current);
    clearTimeout(modalTimer.current);
    setShowModal(false);

    idleTimer.current = setTimeout(() => {
      const modalTime = new Date().toLocaleTimeString();
      console.log(` [${modalTime}] Idle timeout reached (${IDLE_TIMEOUT / 1000}s). Showing modal...`);
      setShowModal(true);

      modalTimer.current = setTimeout(() => {
        const logoutTime = new Date().toLocaleTimeString();
        console.log(`🚫 [${logoutTime}] No activity during modal (${MODAL_TIMEOUT / 1000}s). Logging out...`);
        handleLogout();
      }, MODAL_TIMEOUT);
    }, IDLE_TIMEOUT);

    // Update last activity timestamp in localStorage
    const timestamp = Date.now().toString();
    localStorage.setItem('lastActivity', timestamp);

    // Notify other tabs via BroadcastChannel if available
    if (broadcastChannel.current) {
      broadcastChannel.current.postMessage({
        type: 'ACTIVITY_DETECTED',
        timestamp: timestamp,
        tabId: Math.random().toString(36).substr(2, 9)
      });
    }
  }, [handleLogout]);

  // Handle cross-tab communication
  const handleCrossTabMessage = useCallback((event) => {
    const { type } = event.data;

    if (type === 'ACTIVITY_DETECTED') {
      console.log("📡 Received activity update from another tab");
      // Reset timers when activity is detected in another tab
      clearTimeout(idleTimer.current);
      clearTimeout(modalTimer.current);
      setShowModal(false);

      // Restart the idle timer
      idleTimer.current = setTimeout(() => {
        console.log(" Idle timeout reached. Showing modal...");
        setShowModal(true);

        modalTimer.current = setTimeout(() => {
          console.log("🚫 No activity during modal. Logging out...");
          handleLogout();
        }, MODAL_TIMEOUT);
      }, IDLE_TIMEOUT);
    } else if (type === 'LOGOUT' || type === 'IMMEDIATE_LOGOUT') {
      console.log("📡 Received logout signal from another tab");
      // The centralized logout handler will handle the actual logout
      // Just set session as invalid here
      setIsSessionValid(false);
    }
  }, [handleLogout]);

  // Handle localStorage changes (fallback for older browsers)
  const handleStorageChange = useCallback((event) => {
    if (event.key === 'lastActivity') {
      console.log("📡 Received activity update from localStorage");
      resetIdleTimer();
    } else if ((event.key === 'logout' && event.newValue === 'true') ||
      (event.key === 'logout_immediate' && event.newValue)) {
      console.log("📡 Received logout from localStorage");
      // The centralized logout handler will handle the redirect
      setIsSessionValid(false);
    }
  }, [resetIdleTimer]);

  useEffect(() => {
    const isLoginRoute = loginRoutes.includes(location.pathname);
    const isOtpPage = location.pathname === '/otp';

    if (!isLoginRoute || isOtpPage) {
      // Initialize BroadcastChannel for cross-tab communication
      if (typeof BroadcastChannel !== 'undefined') {
        broadcastChannel.current = new BroadcastChannel('session-sync');
        broadcastChannel.current.onmessage = handleCrossTabMessage;
      }

      // Add storage event listener for fallback
      window.addEventListener('storage', handleStorageChange);

      checkSessionValidity();

      // Only set up activity handlers for non-OTP pages
      if (!isOtpPage) {
        let activityCount = 0;

        const activityHandler = (event) => {
          activityCount++;
          const now = new Date().toLocaleTimeString();

          // Log every 10th activity to reduce console spam
          if (activityCount % 10 === 1) {
            console.log(`🎯 [${now}] Activity event: ${event.type} on ${event.target?.tagName || 'unknown'} (count: ${activityCount})`);
          }

          resetIdleTimer();
        };

        const events = ['click', 'keydown', 'scroll', 'touchstart'];
        console.log(`🎯 [${new Date().toLocaleTimeString()}] Setting up activity listeners for events: ${events.join(', ')}`);

        // Add listeners with error handling
        events.forEach(event => {
          try {
            window.addEventListener(event, activityHandler, { passive: true });
            console.log(`Successfully added listener for: ${event}`);
          } catch (err) {
            console.error(`Failed to add listener for ${event}:`, err);
          }
        });

        console.log(`🎯 Initial timer setup - calling resetIdleTimer()`);
        resetIdleTimer();

        return () => {
          console.log(`🎯 Cleaning up activity listeners`);
          events.forEach(event =>
            window.removeEventListener(event, activityHandler)
          );
          window.removeEventListener('storage', handleStorageChange);
          if (broadcastChannel.current) {
            broadcastChannel.current.close();
          }
          clearTimeout(idleTimer.current);
          clearTimeout(modalTimer.current);
        };
      } else {
        console.log(`🎯 Login Code page detected - skipping activity listeners`);
        // For OTP page, just clean up on unmount
        return () => {
          window.removeEventListener('storage', handleStorageChange);
          if (broadcastChannel.current) {
            broadcastChannel.current.close();
          }
        };
      }
    } else {
      clearTimeout(idleTimer.current);
      clearTimeout(modalTimer.current);
      setShowModal(false);
    }
  }, [location.pathname, checkSessionValidity, handleCrossTabMessage, handleStorageChange, resetIdleTimer]);

  const handleContinue = async () => {
    console.log('🔵 Continue button clicked - manually resetting timer');

    await checkSessionValidity();
    if (isSessionValid) {
      // Manually reset timers (bypass the showModal check)
      clearTimeout(idleTimer.current);
      clearTimeout(modalTimer.current);
      setShowModal(false);

      // Restart idle timer
      idleTimer.current = setTimeout(() => {
        const modalTime = new Date().toLocaleTimeString();
        console.log(` [${modalTime}] Idle timeout reached (${IDLE_TIMEOUT / 1000}s). Showing modal...`);
        setShowModal(true);

        modalTimer.current = setTimeout(() => {
          const logoutTime = new Date().toLocaleTimeString();
          console.log(`🚫 [${logoutTime}] No activity during modal (${MODAL_TIMEOUT / 1000}s). Logging out...`);
          handleLogout();
        }, MODAL_TIMEOUT);
      }, IDLE_TIMEOUT);

      // Update last activity timestamp
      const timestamp = Date.now().toString();
      localStorage.setItem('lastActivity', timestamp);

      // Notify other tabs about session extension
      if (broadcastChannel.current) {
        broadcastChannel.current.postMessage({
          type: 'ACTIVITY_DETECTED',
          timestamp: timestamp,
          tabId: Math.random().toString(36).substr(2, 9)
        });
      }

      console.log('Timer manually reset - session continues');
    }
  };

  return { isSessionValid, showModal, handleContinue, handleLogout, resetIdleTimer };
};

export default useSessionManager;